import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchBlogs, setCurrentPage } from '../../redux/features/blogsSlice';
import BlogCard from './BlogCard';
import AdSlot from '../Ads/AdSlot';

export const BlogGrid = () => {
    const dispatch = useDispatch();
    const { items: blogs, status, error, pagination } = useSelector(state => state.blogs);
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const { currentPage, totalPages, pageSize } = pagination;

    // Use ref to track the last page we fetched to prevent unnecessary refetches
    const lastFetchedPage = useRef(null);

    // Fetch blogs only when page actually changes or on initial load
    useEffect(() => {
        if (status !== 'loading' && (lastFetchedPage.current !== currentPage)) {
            lastFetchedPage.current = currentPage;
            dispatch(fetchBlogs({ page: currentPage, pageSize }));
        }
    }, [dispatch, currentPage, pageSize, status]);

    // Format date helper - memoized
    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '';
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp.toDate();
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);

    // Memoize filtered blogs
    const filteredBlogs = useMemo(() => {
        if (!searchTerm) {
            return blogs.filter(blog => blog.published);
        }

        return blogs.filter(blog =>
            blog.published &&
            (blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                blog.content?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [blogs, searchTerm]);

    // Handle input change without triggering search
    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
    }, []);

    // Trigger search only on Enter or button click
    const executeSearch = useCallback(() => {
        setSearchTerm(inputValue);
        // Reset to first page when searching
        dispatch(setCurrentPage(1));
    }, [dispatch, inputValue]);

    // Handle key press for Enter key
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            executeSearch();
        }
    }, [executeSearch]);

    const handlePreviousPage = useCallback(() => {
        if (currentPage > 1 && status !== 'loading') {
            dispatch(setCurrentPage(currentPage - 1));
        }
    }, [currentPage, dispatch, status]);

    const handleNextPage = useCallback(() => {
        if (currentPage < totalPages && status !== 'loading') {
            dispatch(setCurrentPage(currentPage + 1));
        }
    }, [currentPage, totalPages, dispatch, status]);

    // Memoize loading and error states
    const isLoading = useMemo(() => status === 'loading', [status]);
    const hasError = useMemo(() => status === 'failed', [status]);

    if (isLoading && blogs.length === 0) {
        return (
            <div className="container min-h-screen flex items-center justify-center">
                <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="animate-spin" size={24} />
                    <span>Loading articles...</span>
                </div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="container min-h-screen flex items-center justify-center">
                <div className="text-red-500 text-center">
                    <p className="text-xl font-semibold mb-2">Error loading articles</p>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 md-px-8">
            <div className="mb-8 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
                <h1 className="text-3xl font-bold text-gray-900">Latest Articles</h1>

                <div className="relative max-w-md w-full">
                    <div className="relative flex">
                        <input
                            type="text"
                            placeholder="Search articles..."
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            className="w-full px-4 py-2 pl-10 pr-12 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                        />
                        <button
                            onClick={executeSearch}
                            className="px-4 py-2 bg-green-600 text-white rounded-r-lg hover:bg-green-700 transition-colors"
                        >
                            Search
                        </button>
                        <Search
                            size={20}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                    </div>
                    {searchTerm && (
                        <div className="mt-2 text-sm text-gray-500">
                            Searching for: "{searchTerm}"
                            <button
                                className="ml-2 text-green-600 hover:underline"
                                onClick={() => {
                                    setSearchTerm('');
                                    setInputValue('');
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isLoading && blogs.length > 0 && (
                <div className="flex justify-center my-4">
                    <Loader2 className="animate-spin" size={24} />
                </div>
            )}

            {filteredBlogs.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">
                        {searchTerm
                            ? "No articles found matching your search."
                            : "No articles available yet."
                        }
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Main content area - 9 columns on lg screens */}
                    <div className="lg:col-span-9">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {filteredBlogs.map((blog, index) => (
                                <React.Fragment key={blog.id}>
                                    <Link
                                        to={`/blog/${blog.id}`}
                                        className="transform transition-transform hover:scale-[1.02]"
                                    >
                                        <BlogCard
                                            image={blog.heroImage || "https://via.placeholder.com/800x400"}
                                            title={blog.title}
                                            excerpt={blog.excerpt}
                                            author={blog.author}
                                            date={formatDate(blog.createdAt)}
                                            readTime={blog.readTime || "5 min read"}
                                            categories={blog.categories || []}
                                        />
                                    </Link>

                                    {/* Insert full-width in-feed ad every 6 posts */}
                                    {(index + 1) % 6 === 0 && index !== filteredBlogs.length - 1 && (
                                        <div className="col-span-1 md:col-span-2 xl:col-span-3 my-4">
                                            <AdSlot
                                                type="in-feed"
                                                position={index < filteredBlogs.length / 2 ? 'top' : 'bottom'}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Sidebar area - 3 columns on lg screens */}
                    <div className="lg:col-span-3">
                        <div className="lg:sticky lg:top-20 space-y-8">


                            <AdSlot type="sidebar" position="top" />
                            <AdSlot type="sidebar" position="middle" />

                            <AdSlot type="sidebar" position="bottom" className="mt-6" />
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && !searchTerm && (
                <div className="flex justify-center items-center space-x-4 mt-8 mb-4">
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || isLoading}
                        className={`flex items-center px-4 py-2 rounded-md ${currentPage === 1 || isLoading
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <ChevronLeft size={20} />
                        <span>Previous</span>
                    </button>

                    <div className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </div>

                    <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages || isLoading}
                        className={`flex items-center px-4 py-2 rounded-md ${currentPage === totalPages || isLoading
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <span>Next</span>
                        <ChevronRight size={20} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default BlogGrid;